import React, { useEffect, useState, useMemo } from 'react';
import SectionHeader from '../components/ui/SectionHeader';
import Button from '../components/ui/Button';
import { useAuth } from '../components/AuthContext';
import SunEditor from 'suneditor-react';
import 'suneditor/dist/css/suneditor.min.css';
import SeoAnalysis from '../components/ui/SeoAnalysis'; // Import the new component

function AdminPage() {
  const [activeTab, setActiveTab] = useState('blog');
  const [messages, setMessages] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { logout } = useAuth();

  const [isEditing, setIsEditing] = useState(false);
  const [currentPost, setCurrentPost] = useState({ id: null, title: '', content: '', category_id: '', meta_description: '', meta_keywords: '' });
  const [featuredImage, setFeaturedImage] = useState(null);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [messagesRes, blogRes, categoriesRes] = await Promise.all([
          fetch('/api/messages'),
          fetch('/api/blog_posts'),
          fetch('/api/categories')
        ]);
        if (!messagesRes.ok || !blogRes.ok || !categoriesRes.ok) throw new Error(`HTTP error!`);
        const messagesData = await messagesRes.json();
        const blogData = await blogRes.json();
        const categoriesData = await categoriesRes.json();
        setMessages(messagesData);
        setBlogPosts(blogData);
        setCategories(categoriesData);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    const categoryName = e.target.categoryName.value;
    try {
      const response = await fetch('/api/admin_categories_manage', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: categoryName }),
      });
      if (!response.ok) throw new Error('Failed to add category');
      const newCategory = await response.json();
      setCategories([...categories, { id: newCategory.id, name: categoryName }]);
      e.target.categoryName.value = '';
    } catch (error) {
      setError({ message: error.message });
    }
  };

  const handleDeleteCategory = async (id) => {
    if (window.confirm('Bu kategoriyi silmek istediğinizden emin misiniz? Bu kategoriye ait yazılar kategorisiz kalacaktır.')) {
      try {
        const response = await fetch(`/api/admin_categories_manage?id=${id}`, { method: 'DELETE' });
        if (!response.ok) throw new Error('Failed to delete category');
        setCategories(categories.filter(cat => cat.id !== id));
      } catch (error) {
        setError(error);
      }
    }
  };

  const handleContentChange = (content) => {
    setCurrentPost(prev => ({ ...prev, content }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentPost(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setFeaturedImage(e.target.files[0]);
  };

  const resetForm = () => {
    setIsEditing(false);
    setCurrentPost({ id: null, title: '', content: '', category_id: '', meta_description: '', meta_keywords: '' });
    setFeaturedImage(null);
    if (document.getElementById('featuredImage')) {
        document.getElementById('featuredImage').value = null;
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setError(null); // Clear previous errors
    const url = '/api/admin_blog';
    const method = 'POST';

    try {
        const postData = { ...currentPost };
        const response = await fetch(url, {
            method,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(postData),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ message: 'Sunucudan geçersiz yanıt alındı.' }));
            throw new Error(errorData.message || 'Yazı kaydedilemedi.');
        }

        const result = await response.json();
        const savedPostId = result.id;

        if (featuredImage) {
            const formData = new FormData();
            formData.append('image', featuredImage);
            formData.append('id', savedPostId);

            const imgResponse = await fetch(url, {
                method: 'POST',
                body: formData,
            });

            if (!imgResponse.ok) {
                const imgErrorData = await imgResponse.json().catch(() => ({ message: 'Resim yüklenirken geçersiz yanıt alındı.' }));
                throw new Error(imgErrorData.message || 'Resim yüklenemedi.');
            }
        }

        const blogRes = await fetch('/api/blog_posts');
        const blogData = await blogRes.json();
        setBlogPosts(blogData);
        resetForm();

    } catch (error) {
        setError({ message: error.message });
    }
  };

  const handleEditClick = (post) => {
    setIsEditing(true);
    setCurrentPost(post);
    setActiveTab('blog'); // Switch to blog tab to show the form
    window.scrollTo(0, 0); // Scroll to top to see the form
  };

  const handleDeleteClick = async (id) => {
    if (window.confirm('Bu yazıyı silmek istediğinizden emin misiniz?')) {
      try {
        const response = await fetch(`/api/admin_blog?id=${id}`, { method: 'DELETE' });
        if (!response.ok) throw new Error('Failed to delete post');
        setBlogPosts(blogPosts.filter(post => post.id !== id));
      } catch (error) {
        setError(error);
      }
    }
  };

  const editor = useMemo(() => (
    <SunEditor
        setContents={currentPost.content}
        onChange={handleContentChange}
        setOptions={{
            height: 250,
            buttonList: [
                ['undo', 'redo'],
                ['font', 'fontSize', 'formatBlock'],
                ['bold', 'underline', 'italic', 'strike', 'subscript', 'superscript'],
                ['removeFormat'],
                ['outdent', 'indent'],
                ['align', 'horizontalRule', 'list', 'lineHeight'],
                ['table', 'link', 'image'],
                ['fullScreen', 'showBlocks', 'codeView'],
            ]
        }}
    />
  ), [currentPost.content]);

  if (loading) return <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center"><p>Yönetim paneli yükleniyor...</p></div>;

  return (
    <div className="min-h-screen bg-gray-900 text-white py-12 lg:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-12 max-w-3xl mx-auto text-center relative">
          <SectionHeader subtitle="ADMİN PANELİ" title="İçerik Yönetimi" description="Gelen mesajları ve blog yazılarını buradan yönetebilirsiniz." centered={true} />
          <div className="absolute top-0 right-0 mt-4 mr-4">
            <Button onClick={logout} variant="secondary" size="sm">Çıkış Yap</Button>
          </div>
        </div>

        <div className="border-b border-gray-700 mb-8">
            <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                <button onClick={() => setActiveTab('blog')} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'blog' ? 'border-blue-500 text-blue-400' : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-500'}`}>Blog Yönetimi</button>
                <button onClick={() => setActiveTab('messages')} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'messages' ? 'border-blue-500 text-blue-400' : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-500'}`}>Mesajlar</button>
                <button onClick={() => setActiveTab('settings')} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'settings' ? 'border-blue-500 text-blue-400' : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-500'}`}>Site Ayarları</button>
            </nav>
        </div>

        {error && <div className="bg-red-500/20 text-red-300 p-4 rounded-md mb-6">Hata: {error.message}</div>}

        <div style={{ display: activeTab === 'blog' ? 'block' : 'none' }}>
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700/50 p-6 shadow-xl">
                <form onSubmit={handleFormSubmit}>
                    <h3 className="text-xl font-semibold text-white mb-4">{isEditing ? 'Yazıyı Düzenle' : 'Yeni Yazı Ekle'}</h3>
                    <div className="mb-4">
                        <label htmlFor="title" className="block text-sm font-medium text-gray-300 mb-1">Başlık</label>
                        <input type="text" id="title" name="title" value={currentPost.title} onChange={handleInputChange} className="w-full bg-gray-900 border border-gray-700 rounded-md px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500" required />
                    </div>
                    <div className="mb-4">
                        <label htmlFor="category_id" className="block text-sm font-medium text-gray-300 mb-1">Kategori</label>
                        <select
                            id="category_id"
                            name="category_id"
                            value={currentPost.category_id || ''}
                            onChange={handleInputChange}
                            className="w-full bg-gray-900 border border-gray-700 rounded-md px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">Kategori Seçin</option>
                            {categories.map(cat => (
                                <option key={cat.id} value={cat.id}>{cat.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-300 mb-1">İçerik</label>
                        {editor}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                        <div>
                            <label htmlFor="meta_description" className="block text-sm font-medium text-gray-300 mb-1">Meta Açıklama</label>
                            <textarea id="meta_description" name="meta_description" value={currentPost.meta_description || ''} onChange={handleInputChange} rows="4" className="w-full bg-gray-900 border border-gray-700 rounded-md px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Yazınız için kısa ve çekici bir açıklama girin. (Önerilen: 120-155 karakter)"></textarea>
                        </div>
                        <div>
                            <label htmlFor="meta_keywords" className="block text-sm font-medium text-gray-300 mb-1">Meta Anahtar Kelimeler</label>
                            <input type="text" id="meta_keywords" name="meta_keywords" value={currentPost.meta_keywords || ''} onChange={handleInputChange} className="w-full bg-gray-900 border border-gray-700 rounded-md px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Virgülle ayrılmış anahtar kelimeler girin." />
                        </div>
                    </div>

                    <SeoAnalysis 
                        title={currentPost.title}
                        content={currentPost.content}
                        description={currentPost.meta_description}
                        keywords={currentPost.meta_keywords}
                    />

                    <div className="mb-4 mt-6">
                        <label htmlFor="featuredImage" className="block text-sm font-medium text-gray-300 mb-1">Öne Çıkan Görsel</label>
                        <input type="file" id="featuredImage" onChange={handleFileChange} className="w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-500 file:text-white hover:file:bg-blue-600"/>
                    </div>
                    <div className="flex justify-end">
                        {isEditing && <Button type="button" onClick={resetForm} variant="secondary" className="mr-2">İptal</Button>}
                        <Button type="submit">{isEditing ? 'Güncelle' : 'Yayınla'}</Button>
                    </div>
                </form>
                <div className="mt-8">
                    <h3 className="text-xl font-semibold text-white mb-4">Kategori Yönetimi</h3>
                    <div className="bg-gray-900 border border-gray-700 rounded-md p-4 mb-8">
                        <form onSubmit={handleCategorySubmit} className="flex items-center">
                            <input
                                type="text"
                                name="categoryName"
                                placeholder="Yeni kategori adı"
                                className="w-full bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 mr-2"
                                required
                            />
                            <Button type="submit">Ekle</Button>
                        </form>
                        <div className="overflow-x-auto mt-4">
                            <table className="min-w-full divide-y divide-gray-700">
                                <thead className="bg-gray-700"><tr><th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Kategori Adı</th><th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-300 uppercase tracking-wider">İşlemler</th></tr></thead>
                                <tbody className="divide-y divide-gray-800">
                                    {categories.map((cat) => (<tr key={cat.id}><td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-200">{cat.name}</td><td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium"><Button onClick={() => handleDeleteCategory(cat.id)} variant="danger" size="sm">Sil</Button></td></tr>))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-4">Mevcut Yazılar</h3>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-700">
                            <thead className="bg-gray-700"><tr><th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Başlık</th><th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Tarih</th><th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-300 uppercase tracking-wider">İşlemler</th></tr></thead>
                            <tbody className="divide-y divide-gray-800">
                                {blogPosts.map((post) => (<tr key={post.id}><td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-200">{post.title}</td><td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{new Date(post.created_at).toLocaleDateString()}</td><td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium"><a href={`/blog/${post.slug}`} target="_blank" rel="noopener noreferrer"><Button variant="info" size="sm" className="mr-2">Görüntüle</Button></a><Button onClick={() => handleEditClick(post)} variant="secondary" size="sm" className="mr-2">Düzenle</Button><Button onClick={() => handleDeleteClick(post.id)} variant="danger" size="sm">Sil</Button></td></tr>))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>

        <div style={{ display: activeTab === 'messages' ? 'block' : 'none' }}>
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700/50 p-6 shadow-xl overflow-x-auto">
                {messages.length === 0 ? <p className="text-center text-gray-400">Henüz hiç mesaj yok.</p> : <table className="min-w-full divide-y divide-gray-700"><thead className="bg-gray-700"><tr><th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Ad Soyad</th><th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Email</th><th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Mesaj</th><th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Tarih</th></tr></thead><tbody className="divide-y divide-gray-800">{messages.map((msg) => (<tr key={msg.id} className="hover:bg-gray-700 transition-colors"><td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{msg.name}</td><td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{msg.email}</td><td className="px-6 py-4 text-sm text-gray-300 max-w-xs truncate">{msg.message}</td><td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{new Date(msg.created_at).toLocaleString()}</td></tr>))}</tbody></table>}
            </div>
        </div>

        <div style={{ display: activeTab === 'settings' ? 'block' : 'none' }}>
             <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl border border-gray-700/50 p-6 shadow-xl">
                <h3 className="text-xl font-semibold text-white mb-4">Site Ayarları</h3>
                <p className="text-gray-400">Bu bölüm yapım aşamasındadır.</p>
            </div>
        </div>

      </div>
    </div>
  );
}

export default AdminPage;
